import type { ReactNode } from 'react';

import { HtmlEntityType } from '../../enums/HtmlEntityType';

const graphemeSegmenter = new Intl.Segmenter(undefined, {
  granularity: 'grapheme',
});

let measurementCanvas: HTMLCanvasElement | undefined;

function getMeasurementContext() {
  if (typeof document === 'undefined') return null;
  measurementCanvas ??= document.createElement('canvas');

  return measurementCanvas.getContext('2d');
}

function segmentGraphemes(text: string) {
  return Array.from(graphemeSegmenter.segment(text), ({ segment }) => segment);
}

/**
 * Largest grapheme count whose slice still fits `maxWidth`, found by binary
 * search so a long string costs ~log2(n) measurements instead of n.
 */
function fitGraphemeCount(
  context: CanvasRenderingContext2D,
  total: number,
  maxWidth: number,
  slice: (count: number) => string,
) {
  let low = 0;
  let high = total;

  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (context.measureText(slice(middle)).width <= maxWidth) {
      low = middle;
    } else {
      high = middle - 1;
    }
  }

  return low;
}

export function resolveFont(element: HTMLElement) {
  const style = getComputedStyle(element);

  return `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
}

/**
 * Builds `prefix…suffix` for `availableWidth`. Both sides are measured against
 * half of the width left over after the ellipsis, so the ellipsis lands on the
 * visual midpoint, neither side can starve the other, and no slack is left
 * beside the ellipsis.
 */
export function truncateCenterToWidth(
  text: string,
  availableWidth: number,
  font: string,
) {
  const context = getMeasurementContext();
  if (!context || availableWidth <= 0) return text;

  context.font = font;
  if (context.measureText(text).width <= availableWidth) return text;

  const halfWidth =
    (availableWidth - context.measureText(HtmlEntityType.Ellipsis).width) / 2;
  if (halfWidth <= 0) return HtmlEntityType.Ellipsis;

  const graphemes = segmentGraphemes(text);
  const total = graphemes.length;
  const startCount = fitGraphemeCount(context, total, halfWidth, (count) =>
    graphemes.slice(0, count).join(''),
  );
  const endCount = fitGraphemeCount(context, total, halfWidth, (count) =>
    graphemes.slice(total - count).join(''),
  );

  if (startCount + endCount >= total) return text;

  const start = graphemes.slice(0, startCount).join('').trimEnd();
  const end = graphemes
    .slice(total - endCount)
    .join('')
    .trimStart();

  return `${start}${HtmlEntityType.Ellipsis}${end}`;
}

export function resolveTruncateLines(truncate: boolean | number | undefined) {
  if (typeof truncate === 'number') return truncate;
  if (truncate) return 1;
  return undefined;
}

export function resolveTooltipText(
  overflown: boolean,
  children: ReactNode,
  element: HTMLElement,
) {
  if (!overflown) return undefined;
  if (typeof children === 'string') return children;
  return element.textContent ?? undefined;
}
